import { InputText } from "primereact/inputtext"
import { ChangeEvent, SyntheticEvent, useContext, useEffect, useState } from "react"
import { Dropdown, DropdownChangeParams } from "primereact/dropdown"
import { useConstants } from "../../../api/queries/useConstants"
import { classNames } from "primereact/utils"
import commonStyles from "../../common.module.css"
import FieldError, { FormErrors } from "../../FieldError"
import { Address, AddressDetailResponseData, CreateAddressPayload } from "../../../api/types/address"
import { Button } from "primereact/button"
import { useCreateAddress } from "../../../api/queries/useCreateAddress"
import { useNavigate, useParams } from "react-router-dom"
import { useUpdateAddress } from "../../../api/queries/useUpdateAddress"
import { useAddress } from "../../../api/queries/useAddress"
import { validateAddress } from "./validate"
import { useAddresses } from "../../../api/queries/useAddresses"
import userContext from "../../../context/user"
import { useDeleteAddress } from "../../../api/queries/useDeleteAddress"
import { scrollToFirstInvalidElement } from "../../../common/util"
import { countryItemTemplate, countryValueTemplate } from "../../common/country"

type Params = "uuid"

export default function AddressForm() {
  const navigate = useNavigate()
  const { uuid } = useParams<Params>()
  const createAddress = useCreateAddress()
  const updateAddress = useUpdateAddress(uuid)
  const { data: addressDetail } = useAddress(uuid)
  const { user } = useContext(userContext)
  const deleteAddress = useDeleteAddress(uuid)

  // get main address
  const { data: addresses } = useAddresses({
    limit: 1,
    offset: 0,
    fIsMain: true,
  })

  const { data: constants } = useConstants()
  const [address, setAddress] = useState<CreateAddressPayload>({
    name: "",
    street: "",
    streetDetails: "",
    zip: "",
    city: "",
    country: "",
    ic: "",
    dic: "",
    bankAccount: "",
    bankAccountPrefix: "",
    bankCode: "",
    // fixed data for main address
    isMain: false,
  })

  const [addressErrors, setAddressErrors] = useState<FormErrors<Address>>()

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, name: event.target.value })
    resetAddressErrorsFor(["name"])
  }

  const handleStreetChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, street: event.target.value })
    resetAddressErrorsFor(["street"])
  }

  const handleStreetDetailsChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, streetDetails: event.target.value })
  }

  const handleZipChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, zip: event.target.value })
    resetAddressErrorsFor(["zip"])
  }

  const handleCityChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, city: event.target.value })
    resetAddressErrorsFor(["city"])
  }

  const handleCountryChange = (event: DropdownChangeParams): void => {
    setAddress({ ...address, country: event.target.value })
    resetAddressErrorsFor(["country"])
  }

  const handleICChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, ic: event.target.value })
    resetAddressErrorsFor(["ic"])
  }

  const handleDICChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, dic: event.target.value })
    resetAddressErrorsFor(["dic"])
  }

  const handleBankAccountPrefixChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, bankAccountPrefix: event.target.value })
  }

  const handleBankAccountChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setAddress({ ...address, bankAccount: event.target.value })
  }

  const handleBankCodeChange = (event: DropdownChangeParams): void => {
    setAddress({ ...address, bankCode: event.target.value || "" })
  }

  const resetAddressErrorsFor = (names: string[]) => {
    let obj: { [key: string]: string } = {}
    names.forEach((name) => { obj[name] = "" })
    setAddressErrors(Object.assign({}, addressErrors, obj))
  }

  const handleFormSubmit = async (event: SyntheticEvent) => {
    event.preventDefault()

    const [isValid, errors] = validateAddress(address)
    setAddressErrors(errors);

    if (isValid) {
      if (uuid) {
        await updateAddress.mutateAsync(address)
          .then(() => navigate("/addresses"))
          .catch(({ response }) => {
            let { data }: { data: AddressDetailResponseData } = response
            let respErrors: FormErrors<Address> = {}

            data.errors.forEach((error) => {
              if (typeof error.property === "string") {
                respErrors[error.property as keyof Address] = error.message
              }
            })

            setAddressErrors(respErrors)
            scrollToFirstInvalidElement()
          })
      } else {
        await createAddress.mutateAsync(address)
          .then(() => navigate("/addresses"))
          .catch(({ response }) => {
            let { data }: { data: AddressDetailResponseData } = response
            let respErrors: FormErrors<Address> = {}

            data.errors.forEach((error) => {
              if (typeof error.property === "string") {
                respErrors[error.property as keyof Address] = error.message
              }
            })

            setAddressErrors(respErrors)
            scrollToFirstInvalidElement()
          })
      }
    }
  }

  const handleDelete = async () => {
    await deleteAddress.mutateAsync()
    navigate("/addresses")
  }

  useEffect(() => {
    if (addressDetail) {
      const { bankAccount, bankAccountPrefix, bankCode, city, country, street, streetDetails, zip,
        ic, dic, name, isMain, } = addressDetail.results.addressDetail

      setAddress({
        bankAccount: bankAccount || "",
        bankAccountPrefix: bankAccountPrefix || "",
        bankCode: bankCode || "",
        city,
        country,
        street,
        streetDetails: streetDetails || "",
        zip,
        ic: ic || "",
        dic: dic || "",
        name,
        isMain
      })
    }
  }, [addressDetail])

  useEffect(() => {
    if (
      !uuid &&
      !address.name &&
      !address.ic &&
      !address.dic &&
      addresses &&
      addresses.results.addressList.length > 0
    ) {
      const mainAddress = addresses.results.addressList[0]

      if (mainAddress) {
        setAddress({
          ...address,
          name: mainAddress.name,
          ic: mainAddress.ic || "",
          dic: mainAddress.dic || ""
        })
      }
    }
  }, [addresses, address, uuid])

  return (
    <form className={"col-12"} onSubmit={handleFormSubmit}>
      <div className={"p-fluid"}>
        <div className={"block-title block-title_blue px-3 py-2 border-round-top flex justify-content-between h-auto align-items-center"}>
          Pobočka

          {uuid && user?.isAdmin && !address.isMain && (
            <Button type={"button"} label={"Smazat"}
              className={"p-button p-button-sm p-button-danger w-auto p-button-responsive"}
              icon={"pi pi-trash"} onClick={handleDelete} />
          )}
        </div>

        <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className="p-float-label">
                <InputText value={address.name} onChange={handleNameChange}
                  className={classNames({ "p-invalid": !!addressErrors?.name })}
                  disabled={true} />
                <label className={commonStyles.labelRequired}>
                  Název společnost:
                </label>
              </span>
              <FieldError error={addressErrors?.name} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className="p-float-label">
                <InputText value={address.street} onChange={handleStreetChange}
                  className={classNames({ "p-invalid": !!addressErrors?.street })} />
                <label className={commonStyles.labelRequired}>Ulice a číslo domu:</label>
              </span>
              <FieldError error={addressErrors?.street} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className="p-float-label">
                <InputText value={address.streetDetails || ""} onChange={handleStreetDetailsChange}
                  className={classNames({ "p-invalid": !!addressErrors?.street })} />
                <label>Upřesnění:</label>
              </span>
              <FieldError error={addressErrors?.streetDetails} />
              <small className={commonStyles.fieldHelp}>Součást fakturační adresy</small>
            </div>
          </div>

          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className="p-float-label">
                <InputText value={address.zip} onChange={handleZipChange}
                  className={classNames({ "p-invalid": !!addressErrors?.zip })} />
                <label className={commonStyles.labelRequired}>PSČ:</label>
              </span>
              <FieldError error={addressErrors?.zip} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className="p-float-label">
                <InputText value={address.city} onChange={handleCityChange}
                  className={classNames({ "p-invalid": !!addressErrors?.city })} />
                <label className={commonStyles.labelRequired}>Obec:</label>
              </span>
              <FieldError error={addressErrors?.city} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className="p-float-label">
                <Dropdown options={constants?.results.countries} value={address.country}
                  onChange={handleCountryChange}
                  className={classNames({ "p-invalid": !!addressErrors?.country })}
                  itemTemplate={countryItemTemplate} valueTemplate={countryValueTemplate}
                />
                <label className={commonStyles.labelRequired}>Stát:</label>
              </span>
              <FieldError error={addressErrors?.country} />
            </div>
          </div>

          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className="p-float-label">
                <InputText value={address.ic || ""} onChange={handleICChange}
                  className={classNames({ "p-invalid": !!addressErrors?.ic })}
                  disabled={true} />
                <label className={address.country === "CZ" ? commonStyles.labelRequired : null}>IČ:</label>
              </span>
              <FieldError error={addressErrors?.ic} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className="p-float-label">
                <InputText value={address.dic || ""} onChange={handleDICChange}
                  className={classNames({ "p-invalid": !!addressErrors?.dic })}
                  disabled={true} />
                <label className={commonStyles.labelRequired}>DIČ:</label>
              </span>
              <FieldError error={addressErrors?.dic} />
            </div>

            <div className={"col-12"}>
              <div className={"grid"}>
                <div className={"field col-12 md:col-4"}>
                  <span className="p-float-label">
                    <InputText value={address.bankAccountPrefix || ""} onChange={handleBankAccountPrefixChange}
                      className={classNames({ "p-invalid": !!addressErrors?.bankAccountPrefix })} />
                    <label>Předčíslí:</label>
                  </span>
                  <FieldError error={addressErrors?.bankAccountPrefix} />
                </div>
                <div className={"field col-12 md:col-4"}>
                  <span className="p-float-label">
                    <InputText value={address.bankAccount || ""} onChange={handleBankAccountChange}
                      className={classNames({ "p-invalid": !!addressErrors?.bankAccount })} />
                    <label>Číslo účtu:</label>
                  </span>
                  <FieldError error={addressErrors?.bankAccount} />
                </div>
                <div className={"field col-12 md:col-4"}>
                  <span className="p-float-label">
                    <Dropdown options={constants?.results.bankCodes} value={address.bankCode || ""}
                      onChange={handleBankCodeChange} filter={true} filterBy={"label"}
                      emptyFilterMessage={"Nic nenalezeno"}
                      className={classNames({ "p-invalid": !!addressErrors?.bankCode })}
                      showClear={true} />
                    <label>Kód banky:</label>
                  </span>
                  <FieldError error={addressErrors?.bankCode} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={"flex-1 text-right"}>
        <Button type={"submit"} label={"Uložit"} icon={"pi pi-save"}
          className={"p-button p-button-sm p-button-success"} />
      </div>
    </form>
  )
}
